import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass/styled-components';
import {
  AttributeEditor,
  Heading,
  JSONEditor,
  Layout,
  Link,
  Loading,
  NavigationLink,
  Row,
  Section,
  Stepper,
  Subtitle,
  ConfirmModal,
  NavigateGuard,
} from '../components';
import api from '../api';
import validation from '../validation';
import { checkValueType, parseToArray } from '../components/Editor/util';
import { useAppState, useNotification } from '../hooks';
import EditModeButtons from '../components/custom-context/edit-mode-buttons';
import DefaultButtons from '../components/custom-context/default-buttons';
import AddNewButtons from '../components/custom-context/add-new-button';
import { removeEnclosingQuotes } from '../util';

const LINK_TO_DOCS = 'https://developers.liveperson.com/maven-context-warehouse-custom-static-or-function.html';

const formatPayload = (obj, isCcsV2) => {
  if (isCcsV2) {
    const { parameters } = obj.payload.body;
    if (Array.isArray(parameters) && parameters.length === 1) {
      const [firstItem] = parameters;
      return firstItem;
    }
    return parameters;
  }
  const { payload } = obj.payload.body;
  if (Array.isArray(payload) && payload.length === 1) {
    const [firstItem] = payload;
    return firstItem;
  }
  return payload;
};

const formatPolicyForValidation = (obj, isCcsV2) => {
  const newObject = { ...obj };

  const isFaas = newObject.type === 'faas';
  const isStatic = newObject.type === 'static';

  // Format the payload if there is just a single item
  if (isFaas && newObject.payload && newObject.payload.body && (
    newObject.payload.body.payload || newObject.payload.body.parameters)) {
    if (isCcsV2) {
      newObject.payload.body.parameters = formatPayload(newObject, isCcsV2);
    } else {
      newObject.payload.body.payload = formatPayload(newObject, isCcsV2);
    }
  }

  if (isStatic) {
    const valueType = checkValueType(newObject.payload);
    const isList = valueType === 'list';

    if (isList && newObject.payload) {
      newObject.payload = parseToArray(newObject.payload);
    }

    // strip \" or ' character from 1st or last character
    if (typeof newObject.payload === 'string') {
      newObject.payload = removeEnclosingQuotes(newObject.payload);
    }
  }

  return newObject;
};

function CustomContextPage(props) {
  const {
    name, subtitle,
  } = props;

  const isMounted = useRef(false);

  const [contexts, setContexts] = useState([]);
  const [temporaryContext, setTemporaryContext] = useState({});
  const [selectedContext, setSelectedContext] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingContexts, setIsFetchingContexts] = useState(null);
  const [editorVersion, setEditorVersion] = useState(null);
  const [isValid, setIsValid] = useState(true);
  const {
    isCcsV2, isLoading: isAppStateLoading, features, accountId: brandId, themeMode,
  } = useAppState();
  const { addNotification } = useNotification();
  const [confirmModalProps, setConfirmModalProps] = useState(null);

  const isEditorVersion2 = editorVersion === 'v2';
  const getAttributeName = (item) => item.name || item.varName;

  const selectedIndex = (list) => {
    const index = list.findIndex(
      (item) => item.name === temporaryContext && getAttributeName(temporaryContext),
    );
    return Math.max(index, 0);
  };

  const getEditorVersion = async () => {
    try {
      const featureResult = await api.mavenFeatures.getFeature('maven.feature.attributeEditorV2', brandId);
      if (isMounted.current) {
        setEditorVersion(featureResult ? 'v2' : 'v1');
      }
    } catch (error) {
      if (isMounted.current) {
        setEditorVersion('v1');
      }
    }
  };

  const getContextData = async () => {
    if (!isFetchingContexts) {
      if (isMounted.current) {
        setIsFetchingContexts(true);
      }
      try {
        const data = isCcsV2
          ? await api.ccsV2.getCustomContexts(brandId)
          : await api.mavenConfig.getCustomContexts(brandId);

        if (isMounted.current) {
          setContexts(data);
          setTemporaryContext(data.length > 0 ? data[selectedIndex(data)] : {});
        }
      } catch (error) {
        // There is currently no implementation to distinguish between a brand that does not exist,
        // and a brand that has no custom attributes yet... so for the moment, we ignore this error
        if (error.response && error.response.status !== 404) {
          addNotification(error, 'error');
        }
      }
      if (isMounted.current) {
        setIsFetchingContexts(false);
      }
    }
  };

  const confirmDeleteContext = async () => {
    if (isMounted.current) {
      setConfirmModalProps({
        show: false,
      });
      setIsDeleting(true);
      let data;

      try {
        if (isCcsV2) {
          await api.ccsV2.deleteCustomContext(brandId, temporaryContext.varName);
          data = await api.ccsV2.getCustomContexts(brandId);
        } else {
          await api.mavenConfig.deleteCustomContext(brandId, temporaryContext);
          data = await api.mavenConfig.getCustomContexts(brandId);
        }

        if (isMounted.current) {
          setContexts(data);
          // Select the first item in the list after deleting
          setTemporaryContext(data.length ? data[0] : {});
        }
        addNotification('Context deleted successfully', 'success');
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsDeleting(false);
      }
    }
  };

  const deleteContext = async () => {
    if (!isDeleting) {
      setConfirmModalProps({
        show: true,
        detail: `Are you sure you want to delete the context "${getAttributeName(temporaryContext)}"`,
        onConfirm: confirmDeleteContext,
        onCancel: () => {
          if (isMounted.current) {
            setConfirmModalProps({
              show: false,
            });
          }
        },
      });
    }
  };

  const editContext = () => {
    // Set a selected context so that we can preserve attributes when editing v1 context as JSON
    const newObject = { ...temporaryContext };
    delete newObject.name;
    delete newObject.isTemporary;
    setSelectedContext(JSON.stringify(newObject, undefined, 4));
    setIsEditing(true);
  };

  const cleanAttributeErrors = () => {
    if (isEditorVersion2) {
      // Reset the attribute object
      setTemporaryContext(validation.removeErrorKeysFromJson(temporaryContext));
    }
  };

  const validateAttribute = (obj) => {
    if (!isEditorVersion2) {
      setTemporaryContext(obj);
      return true;
    }

    // Remove the isTemporary property so that validation doesn't bork
    const {
      isTemporary,
      isUntitled,
      placeHolderText,
      ...cleanedContext
    } = obj;

    // Removing previous error keys
    const cleanedTemporaryAttribute = validation.removeErrorKeysFromJson(cleanedContext);

    const isFaas = obj.type === 'faas';
    if (isFaas) {
      // Add faas headers
      if (cleanedTemporaryAttribute.payload.body.headers !== []) {
        cleanedTemporaryAttribute.payload.body.headers = [];
      }

      // Format the payload if there is just a single item
      if (isCcsV2) {
        cleanedTemporaryAttribute.payload.body.parameters = formatPayload(
          cleanedTemporaryAttribute, isCcsV2,
        );
      } else {
        cleanedTemporaryAttribute.payload.body.payload = formatPayload(cleanedTemporaryAttribute);
      }
    }

    // Validate
    const {
      passedValidation, validationErrors,
    } = validation.validateAttribute(cleanedTemporaryAttribute, isCcsV2);

    setIsValid(passedValidation);

    if (passedValidation) {
      // Set object WITHOUT error keys
      setTemporaryContext({ isTemporary, ...cleanedTemporaryAttribute });
    } else {
      // Add in the error properties
      const cleanedContextWithErrors = validation.addAttributeErrorProperty(
        cleanedTemporaryAttribute, validationErrors,
      );

      // Set object WITH error keys
      setTemporaryContext({
        isTemporary,
        isUntitled,
        placeHolderText,
        ...cleanedContextWithErrors,
      });
    }

    return passedValidation;
  };

  const parseInput = (context) => {
    try {
      return JSON.parse(context);
    } catch (error) {
      addNotification(error, 'error');
    }
    return false;
  };

  const checkV2Payload = () => {
    const isFaas = temporaryContext.type === 'faas';

    // Only check the payload if the type is json
    if (checkValueType(isFaas ? temporaryContext.payload.body.payload || temporaryContext.payload.body.parameters : temporaryContext.payload) === 'json') {
      try {
        JSON.parse(selectedContext);
      } catch (error) {
        addNotification(error, 'error');
        return false;
      }
    }
    return true;
  };

  const saveContext = async () => {
    if (!isSaving) {
      if (isMounted.current) {
        setIsSaving(true);
      }

      // Parse the context (for v1 policies, v2 policies are ignored)
      const parsedContext = isEditorVersion2 ? temporaryContext : parseInput(selectedContext);
      const isJSONValid = isEditorVersion2 ? checkV2Payload() : typeof parsedContext === 'object';

      if (isJSONValid) {
        // Rebuild the policy with the selected policy, so that we can preserve stripped
        // attributes when editing the policy as JSON
        const updatedContext = { ...temporaryContext, ...parsedContext };

        const {
          isTemporary,
          isUntitled,
          placeHolderText,
          ...cleanedContext
        } = updatedContext;

        const formattedObject = formatPolicyForValidation(cleanedContext, isCcsV2);
        const passedValidation = validateAttribute(formattedObject);

        // remove spaces from name
        formattedObject.name = formattedObject.name ? formattedObject.name.replace(/\s+/g, '') : undefined;

        if (passedValidation) {
          try {
            if (isCcsV2) {
              let saveAttribute = false;
              const attributeName = formattedObject.varName
                ? formattedObject.varName.replace(/\s+/g, '')
                : formattedObject.name;

              // check to see if variable exist, if so and same type update it
              // else throw error and stop
              try {
                const { type } = await api.ccsV2.getCustomAttribute(brandId, attributeName);
                if (type !== formattedObject.type) {
                  addNotification(
                    `${attributeName} already exist as a ${type} attribute. Please recreate using a `
                    + 'different name or delete the existing attribute first.',
                    'error',
                  );
                } else {
                  // variable is the same type, update it...
                  saveAttribute = true;
                }
              } catch (e) {
                // no variable defined, save one...
                saveAttribute = true;
              }

              if (saveAttribute) {
                await api.ccsV2.createCustomContext(
                  formattedObject.type, brandId, attributeName, formattedObject.payload,
                );
              }
            } else if (temporaryContext.isTemporary) {
              await api.mavenConfig.createCustomContext(brandId, formattedObject);
            } else {
              await api.mavenConfig.updateCustomContext(brandId, formattedObject);
            }

            await getContextData();
            if (isMounted.current) {
              setIsEditing(false);
              addNotification('Attribute saved', 'success');
            }
          } catch (error) {
            addNotification(error, 'error');
            // Reset object on error
            setTemporaryContext({
              ...cleanedContext,
              isTemporary: temporaryContext.isTemporary,
              isUntitled: temporaryContext.isUntitled,
              placeHolderText: temporaryContext.placeHolderText,
            });
          }
        } else {
          // If the validation does not pass, set the temporaryPolicy back to the correct version
          setTemporaryContext(updatedContext);
        }
      }

      if (isMounted.current) {
        setIsSaving(false);
      }
    }
  };

  const addNewVar = (newContext) => {
    if (newContext.type === 'static') {
      setTemporaryContext({
        ...newContext,
        isTemporary: true,
        isUntitled: true,
        placeHolderText: 'Enter a new attribute name',
      });
    } else if (newContext.type === 'faas') {
      setTemporaryContext({ ...newContext, isTemporary: true });
    }
    setSelectedContext(JSON.stringify(newContext, undefined, 4));
    setContexts((prev) => [...prev, newContext]);
    setIsEditing(true);
  };

  const cancelCancelSave = () => {
    if (isMounted.current) {
      setConfirmModalProps({
        show: false,
      });
    }
  };

  const confirmCancelSave = async () => {
    if (isMounted.current) {
      setConfirmModalProps({
        show: false,
      });
      const newContexts = contexts.filter((item) => !item.isTemporary);
      setContexts(newContexts);
      setTemporaryContext(newContexts.length ? newContexts[0] : {});
      await getContextData();
      setIsEditing(false);
    }
  };

  const cancelContext = async () => {
    // Reset the error values to a clean state
    cleanAttributeErrors();

    if (temporaryContext.isTemporary) {
      setConfirmModalProps({
        show: true,
        onConfirm: confirmCancelSave,
        onCancel: cancelCancelSave,
        detail: `Are you sure you want to cancel? The context "${getAttributeName(temporaryContext)}" will not be saved`,
      });
    } else {
      await getContextData();
      setIsEditing(false);
    }
  };

  const handleContextClick = (context) => {
    if (!isEditing) {
      setIsEditing(false);
      setTemporaryContext(context);
    }
  };

  const handleTextAreaChange = (event) => {
    setSelectedContext(event.target.value);
    try {
      const parsedJSON = JSON.parse(event.target.value);
      setTemporaryContext({ ...temporaryContext, ...parsedJSON });
    } catch (error) {
      // addNotification(error);
    }
  };

  const hasSelectedContext = Object.keys(temporaryContext).length > 0;

  const isItemSelected = (item) => {
    if (temporaryContext.isTemporary) {
      return hasSelectedContext && item.isTemporary;
    }
    return hasSelectedContext && getAttributeName(item) === getAttributeName(temporaryContext);
  };

  const getConfigButtons = () => {
    if (isEditing) {
      return (
        <EditModeButtons
          cancel={cancelContext}
          save={saveContext}
          isValid={isValid}
          isSaving={isSaving}
        />
      );
    }
    if (hasSelectedContext) {
      return (
        <DefaultButtons
          editContext={editContext}
          deleteContext={deleteContext}
          isDeleting={isDeleting}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      await getEditorVersion();
      await getContextData();
    }
    if (!isAppStateLoading) {
      fetchData();
    }
  }, [isAppStateLoading]); // eslint-disable-line

  const editor = isEditorVersion2
    ? (
      <AttributeEditor
        brandId={brandId}
        addNotification={addNotification}
        themeMode={themeMode}
        isEditing={isEditing}
        temporaryObject={temporaryContext}
        validateObject={validateAttribute}
        setSelectedObject={setSelectedContext}
        includeSdeAttribute={!!features?.['maven.feature.sdePage']}
      />
    ) : (
      <JSONEditor
        themeMode={themeMode}
        isEditing={isEditing}
        selectedObject={temporaryContext}
        selectedString={selectedContext}
        handleChange={handleTextAreaChange}
      />
    );

  return (
    <Box pb={4}>
      <NavigateGuard
        when={isEditing}
        message="Are you sure you want to leave? Your changes will not be saved"
      />
      <Heading>{name}</Heading>
      <Subtitle mb={3}>
        Custom attributes enable you define and use your own data using static attributes (e.g. a
        list of names or emails), or fetching information from an external source at runtime using
        LivePerson Functions (e.g. from Salesforce).
      </Subtitle>
      <Section
        section="custom-getting-started"
        title="Getting started"
        subtitle={(
          <Box>
            Using the Conversation Context Service with AI Powered Routing requires configuring
            several different features in the Conversational AI suite. To complete the remaining
            steps, please see the
            {' '}
            <Link href={LINK_TO_DOCS} target="_blank" rel="noopener noreferrer">
              documentation
            </Link>
            .
          </Box>
        )}
      >
        <Stepper
          steps={[
            { id: 1, title: 'Manage attributes in Conversation Context Service' },
            {
              id: 2,
              title: <NavigationLink to="/intent-content-policies">Build routing policies</NavigationLink>,
            },
            {
              id: 3,
              title: (
                <Box>
                  Connect to Maven using Maven Bot or
                  {' '}
                  <NavigationLink to="/ask-maven">AskMaven</NavigationLink>
                </Box>
              ),
            },
            { id: 4, title: 'Start using policies' },
          ]}
          activeStep={1}
        />
      </Section>

      <Section title="Manage custom attributes" subtitle={subtitle}>
        <Layout
          layoutItems={
            isFetchingContexts !== false
              ? [
                <Flex key="loading" align="center" justifyContent="center" py={3}>
                  <Loading size={24} data-testid="loading-attributes" />
                </Flex>,
              ]
              : contexts.map((context) => (
                <Row
                  themeMode={themeMode}
                  key={`${context.name}-${Math.random()}`} // :(
                  data={context}
                  handleClick={handleContextClick}
                  isActive={isItemSelected(context)}
                  data-testid="layout-row"
                />
              ))
          }
          layoutButtons={isFetchingContexts === false ? (
            <AddNewButtons
              editorVersion={editorVersion}
              isEditing={isEditing}
              isEditorVersion2={isEditorVersion2}
              addNewVar={addNewVar}
            />
          ) : null}
          configButtons={getConfigButtons()}
          configPanel={hasSelectedContext && editorVersion !== null ? editor : null}
          isEditing={isEditing}
        />
      </Section>
      <ConfirmModal {...confirmModalProps} />
    </Box>
  );
}

CustomContextPage.defaultProps = {
  subtitle: undefined,
};

CustomContextPage.propTypes = {
  name: PropTypes.string.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default CustomContextPage;
